import React from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import {
  LocalAudioTrack,
  LocalVideoTrack,
  Participant,
  RemoteAudioTrack,
  RemoteVideoTrack,
} from 'twilio-video';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import {
  useIsTrackSwitchedOff,
  usePublications,
  useTrack,
  useVideoContext,
  useParticipantIsReconnecting
} from '../hooks';
import { AudioLevelIndicator } from './audio-level-indicator';
import { getParticipantNameAndInitials } from './helpers';
import { useUserWithProfileQuery } from '@uplift/queries';

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
  identity: {
    background: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    padding: '0.1em 0.3em 0.1em 0',
    display: 'inline-flex',
    '& svg': {
      marginLeft: '0.3em',
    },
    marginRight: '0.4em',
    alignItems: 'center',
  },
  infoContainer: {
    position: 'absolute',
    zIndex: 2,
    height: '100%',
    width: '100%',
    display: 'flex',
    /* flex-direction: row; */
    flexDirection: 'column-reverse',
  },
  reconnectingContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(40, 42, 43, 0.75)',
    zIndex: 1,
  },
  fullWidth: {
    gridArea: '1 / 1 / 2 / 3',
    [theme.breakpoints.down('md')]: {
      gridArea: '1 / 1 / 3 / 3',
    },
  },
  avatarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'black',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: -1,
  },
  avatarSize: {
    display: 'flex',
    height: '15rem',
    width: '15rem',
    maxWidth: '225px',
    maxHeight: '226px',
    background: '#A568F3',
    fontFamily: 'Futura PT',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '96px'
  },
}));

interface MainParticipantInfoProps {
  participant: Participant;
  children: React.ReactNode;
}

export function MainParticipantInfo({ participant, children, }: MainParticipantInfoProps) {
  const { classes, cx } = useStyles();
  const { room, appointment } = useVideoContext();
  const { data: patient } = useUserWithProfileQuery(appointment?.patientId, { enabled: !!appointment?.patientId });
  const { data: provider } = useUserWithProfileQuery(appointment?.providerId, { enabled: !!appointment?.providerId });
  const { name, initials } = getParticipantNameAndInitials(patient, provider, participant);
  const localParticipant = room?.localParticipant;
  const isLocal = localParticipant === participant;
  const publications = usePublications(participant);
  const videoPublication = publications.find(p => !p.trackName.includes('screen') && p.kind === 'video');
  const screenSharePublication = publications.find(p => p.trackName.includes('screen'));
  const videoTrack = useTrack(screenSharePublication || videoPublication);
  const isVideoEnabled = Boolean(videoTrack);

  const audioPublication = publications.find((p) => p.kind === 'audio');
  const audioTrack = useTrack(audioPublication) as
    | LocalAudioTrack
    | RemoteAudioTrack
    | undefined;

  const isVideoSwitchedOff = useIsTrackSwitchedOff(
    videoTrack as LocalVideoTrack | RemoteVideoTrack
  );
  const isParticipantReconnecting = useParticipantIsReconnecting(participant);

  return (
    <div
      data-cy-main-participant
      data-cy-participant={participant?.identity}
      className={cx(classes.container)}
    >
      <div className={classes.infoContainer}>
        <div style={{ display: 'flex' }}>
          <div className={classes.identity}>
            <AudioLevelIndicator audioTrack={audioTrack} />
            <Typography variant="body1" color="inherit">
              {!isLocal && name}
              {isLocal && 'You'}
            </Typography>
          </div>
          {/* <NetworkQualityLevel participant={localParticipant} /> */}
        </div>
      </div>
      {(!isVideoEnabled || isVideoSwitchedOff) && (
        <div className={classes.avatarContainer}>
          <Avatar className={classes.avatarSize} alt={name}>
            <span>{initials}</span>
          </Avatar>
        </div>
      )}
      {isParticipantReconnecting && (
        <div className={classes.reconnectingContainer}>
          <Typography variant="body1" style={{ color: 'white' }}>
            Reconnecting...
          </Typography>
        </div>
      )}
      {children}
    </div>
  );
}
